import React, { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import {
  User,
  Wallet,
  FileText,
  Shield,
  HelpCircle,
  HeartHandshake,
  Banknote,
  IndianRupee,
  RefreshCw,
  Download,
  PlusCircle,
  CheckCircle2,
  XCircle,
} from 'lucide-react';
import axios from 'axios';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
  DashboardHeader,
  StatCard,
  EmptyState,
  formatCurrency,
  formatDate,
  containerVariants,
  itemVariants,
} from './shared';

const TABS = [
  { id: 'overview', label: 'Overview', icon: Shield },
  { id: 'profile', label: 'My Profile', icon: User },
  { id: 'pension', label: 'My Pension', icon: IndianRupee },
  //{ id: 'nominee', label: 'Nominee', icon: HeartHandshake },
  { id: 'documents', label: 'Documents', icon: FileText },
  //{ id: 'support', label: 'Help & Support', icon: HelpCircle },
];

const formatApiError = (error, fallbackMessage) => {
  const detail = error?.response?.data?.detail;

  if (Array.isArray(detail)) {
    const messages = detail
      .map((item) => item?.msg || item?.message || (typeof item === 'string' ? item : null))
      .filter(Boolean);
    if (messages.length) {
      return messages.join(' • ');
    }
  }

  if (typeof detail === 'string' && detail.trim()) {
    return detail;
  }

  if (detail && typeof detail === 'object') {
    if (detail.msg) {
      return detail.msg;
    }
    if (detail.message) {
      return detail.message;
    }
  }

  if (typeof error?.message === 'string' && error.message.trim()) {
    return error.message;
  }

  return fallbackMessage;
};

const PensionerDashboard = () => {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = searchParams.get('tab') || 'overview';

  const setTab = (tabId, replace = false) => {
    const nextParams = new URLSearchParams(searchParams);
    if (!tabId || tabId === 'overview') {
      nextParams.delete('tab');
    } else {
      nextParams.set('tab', tabId);
    }
    setSearchParams(nextParams, { replace });
  };
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [nominees, setNominees] = useState([]);
  const [submittingNominee, setSubmittingNominee] = useState(false);
  const [nomineeError, setNomineeError] = useState('');
  const [nomineeForm, setNomineeForm] = useState({
    nominee_name: '',
    relationship: '',
    date_of_birth: '',
    gender: 'male',
    benefit_percentage: 100,
    priority: 1,
    is_primary: true,
    bank_name: '',
    account_holder_name: '',
    account_number: '',
    ifsc_code: '',
    remarks: '',
  });
  const [error, setError] = useState('');

  const pensionSummary = useMemo(() => {
    const data = profile || {};
    return {
      ppo_number: data.ppo_number || '',
      pension_category: data.pension_category || '',
      retirement_type: data.retirement_type || '',
      pension_start_date: data.pension_start_date || '',
      retirement_date: data.retirement_date || '',
      basic_pension: Number(data.basic_pension || 0),
      bank_name: data.bank_name || '',
      bank_account_number: data.bank_account_number || '',
      bank_ifsc_code: data.bank_ifsc_code || '',
      bank_branch: data.bank_branch || '',
    };
  }, [profile]);

  const loadProfile = async () => {
    setLoading(true);
    setError('');
    try {
      const profileRes = await axios.get('/v1/pensioners/me');
      const profileData = profileRes.data;
      setProfile(profileData);

      if (profileData?.id) {
        try {
          const nomineesRes = await axios.get(`/v1/pensioners/${profileData.id}/nominees`);
          setNominees(Array.isArray(nomineesRes.data) ? nomineesRes.data : []);
        } catch {
          setNominees([]);
        }
      } else {
        setNominees([]);
      }

      if (profileData?.employee_id) {
        try {
          const docsRes = await axios.get(`/v1/employee-master/${profileData.employee_id}/documents`);
          setDocuments(Array.isArray(docsRes.data) ? docsRes.data : []);
        } catch {
          setDocuments([]);
        }
      } else {
        setDocuments([]);
      }
    } catch (err) {
      setError(formatApiError(err, 'Unable to load pensioner profile'));
      setProfile(null);
      setDocuments([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadProfile();
  }, []);

  const handleNomineeSubmit = async (e) => {
    e.preventDefault();
    if (!profile?.id) return;
    setSubmittingNominee(true);
    setNomineeError('');
    try {
      const payload = {
        ...nomineeForm,
        benefit_percentage: Number(nomineeForm.benefit_percentage),
        priority: Number(nomineeForm.priority),
        is_primary: Boolean(nomineeForm.is_primary),
      };
      await axios.post(`/v1/pensioners/${profile.id}/nominees`, payload);
      setNomineeForm({
        nominee_name: '',
        relationship: '',
        date_of_birth: '',
        gender: 'male',
        benefit_percentage: 100,
        priority: 1,
        is_primary: true,
        bank_name: '',
        account_holder_name: '',
        account_number: '',
        ifsc_code: '',
        remarks: '',
      });
      const nomineesRes = await axios.get(`/v1/pensioners/${profile.id}/nominees`);
      setNominees(Array.isArray(nomineesRes.data) ? nomineesRes.data : []);
    } catch (err) {
      setNomineeError(formatApiError(err, 'Unable to save nominee details'));
    } finally {
      setSubmittingNominee(false);
    }
  };

  const handleDownload = (docId, fileName) => {
    axios.get(`/v1/employee-master/${profile.employee_id}/documents/${docId}/download`, { responseType: 'blob' })
      .then((res) => {
        const url = window.URL.createObjectURL(res.data);
        const a = document.createElement('a');
        a.href = url;
        a.download = fileName || 'document';
        a.click();
        window.URL.revokeObjectURL(url);
      });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="flex items-center gap-3 text-slate-600">
          <RefreshCw className="w-5 h-5 animate-spin" />
          Loading pensioner dashboard...
        </div>
      </div>
    );
  }

  if (error || !profile) {
    return (
      <div className="min-h-screen bg-slate-50">
        <DashboardHeader
          title="Pensioner Dashboard"
          subtitle={user?.employee_name || user?.username || 'Pensioner'}
          icon={User}
        />
        <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <EmptyState
            icon={Shield}
            title="Pensioner profile not available"
            description={error || 'No pensioner profile is linked to this account yet.'}
            size="large"
            actionButton={(
              <button type="button" onClick={loadProfile} className="px-4 py-2 rounded-lg bg-blue-600 text-white">
                Retry
              </button>
            )}
          />
        </main>
      </div>
    );
  }

  const employeeName = profile.employee_name || user?.employee_name || 'Pensioner';
  const employeeCode = profile.employee_code || user?.employee_code || '';

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      <DashboardHeader
        title="Pensioner Dashboard"
        subtitle={`${employeeName}${employeeCode ? ` · ${employeeCode}` : ''}`}
        icon={User}
      >
        <button
          type="button"
          onClick={loadProfile}
          className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 text-sm"
        >
          <RefreshCw className="w-4 h-4" />
          Refresh
        </button>
      </DashboardHeader>

      <nav className="bg-white border-b border-slate-200 sticky top-16 z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex gap-2 overflow-x-auto whitespace-nowrap">
            {TABS.map((item) => (
              <button
                key={item.id}
                type="button"
                onClick={() => setTab(item.id)}
                className={`flex items-center gap-2 px-4 py-3 border-b-2 text-sm font-medium ${tab === item.id ? 'border-blue-600 text-blue-700' : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'}`}
              >
                <item.icon className="w-4 h-4" />
                {item.label}
              </button>
            ))}
          </div>
        </div>
      </nav>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {tab === 'overview' && (
          <motion.div variants={containerVariants} initial="hidden" animate="visible" className="space-y-6">
            <motion.section variants={itemVariants} className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2 bg-white rounded-2xl shadow-sm border border-slate-100 p-6">
                <div className="flex flex-col sm:flex-row sm:items-center gap-5">
                  <div className="w-20 h-20 rounded-full bg-blue-100 flex items-center justify-center">
                    <User className="w-10 h-10 text-blue-600" />
                  </div>
                  <div className="flex-1">
                    <h1 className="text-2xl font-bold text-slate-900">{employeeName}</h1>
                    <p className="text-slate-500 mt-1">{employeeCode}</p>
                    <div className="mt-3 flex flex-wrap gap-2">
                      <span className="px-3 py-1 rounded-full bg-blue-100 text-blue-700 text-xs font-semibold">Pensioner</span>
                      <span className="px-3 py-1 rounded-full bg-emerald-100 text-emerald-700 text-xs font-semibold">{pensionSummary.pension_category || 'Pension'}</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6">
                <p className="text-sm text-slate-500">PPO Number</p>
                <p className="text-2xl font-bold text-slate-900 mt-1">{pensionSummary.ppo_number || '-'}</p>
                <p className="text-xs text-slate-500 mt-3">Retirement Date: {pensionSummary.retirement_date ? formatDate(pensionSummary.retirement_date) : '-'}</p>
              </div>
            </motion.section>

            <section className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
              <StatCard title="Basic Pension" value={pensionSummary.basic_pension} icon={Wallet} color="bg-blue-600" subtitle="Monthly pension amount" />
              <StatCard title="Pension Start" value={pensionSummary.pension_start_date ? formatDate(pensionSummary.pension_start_date) : '-'} icon={Banknote} color="bg-emerald-600" useCountUp={false} />
              <StatCard title="Bank Account" value={pensionSummary.bank_account_number ? `****${String(pensionSummary.bank_account_number).slice(-4)}` : '-'} icon={FileText} color="bg-slate-700" useCountUp={false} />
              <StatCard title="Bank IFSC" value={pensionSummary.bank_ifsc_code || '-'} icon={IndianRupee} color="bg-amber-600" useCountUp={false} />
            </section>
          </motion.div>
        )}

        {tab === 'profile' && (
          <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-sm">
              <div>
                <p className="text-slate-500">Employee Name</p>
                <p className="text-slate-900 font-medium">{employeeName}</p>
              </div>
              <div>
                <p className="text-slate-500">Employee Code</p>
                <p className="text-slate-900 font-medium">{employeeCode}</p>
              </div>
              <div>
                <p className="text-slate-500">Department</p>
                <p className="text-slate-900 font-medium">{profile.department || '-'}</p>
              </div>
              <div>
                <p className="text-slate-500">Designation</p>
                <p className="text-slate-900 font-medium">{profile.designation || '-'}</p>
              </div>
              <div>
                <p className="text-slate-500">Retirement Type</p>
                <p className="text-slate-900 font-medium">{pensionSummary.retirement_type || '-'}</p>
              </div>
              <div>
                <p className="text-slate-500">Remarks</p>
                <p className="text-slate-900 font-medium">{profile.remarks || 'No remarks recorded'}</p>
              </div>
            </div>
          </div>
        )}

        {tab === 'pension' && (
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
            <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6">
              <h2 className="text-lg font-semibold text-slate-900 mb-4">Pension Details</h2>
              <div className="space-y-4 text-sm">
                <div className="flex items-center justify-between">
                  <span className="text-slate-500">PPO Number</span>
                  <span className="font-medium text-slate-900">{pensionSummary.ppo_number || '-'}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-slate-500">Category</span>
                  <span className="font-medium text-slate-900">{pensionSummary.pension_category || '-'}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-slate-500">Retirement Date</span>
                  <span className="font-medium text-slate-900">{pensionSummary.retirement_date ? formatDate(pensionSummary.retirement_date) : '-'}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-slate-500">Pension Start Date</span>
                  <span className="font-medium text-slate-900">{pensionSummary.pension_start_date ? formatDate(pensionSummary.pension_start_date) : '-'}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-slate-500">Monthly Pension</span>
                  <span className="font-semibold text-slate-900">{formatCurrency(pensionSummary.basic_pension)}</span>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6">
              <h2 className="text-lg font-semibold text-slate-900 mb-4">Bank Details</h2>
              <div className="space-y-4 text-sm">
                <div className="flex items-center justify-between">
                  <span className="text-slate-500">Bank Name</span>
                  <span className="font-medium text-slate-900">{pensionSummary.bank_name || '-'}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-slate-500">Account Number</span>
                  <span className="font-medium text-slate-900">{pensionSummary.bank_account_number || '-'}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-slate-500">IFSC Code</span>
                  <span className="font-medium text-slate-900">{pensionSummary.bank_ifsc_code || '-'}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-slate-500">Branch</span>
                  <span className="font-medium text-slate-900">{pensionSummary.bank_branch || '-'}</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {tab === 'nominee' && (
          <div className="space-y-6">
            <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h2 className="text-lg font-semibold text-slate-900">Nominee Management</h2>
                  <p className="text-sm text-slate-500">Add or update nominee details for pension disbursement.</p>
                </div>
                <div className="rounded-full bg-emerald-100 px-3 py-1 text-xs font-semibold text-emerald-700">{nominees.length} record(s)</div>
              </div>

              {nomineeError ? <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{nomineeError}</div> : null}

              <form onSubmit={handleNomineeSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <label className="text-sm text-slate-700">
                  <span className="mb-1 block font-medium">Nominee Name</span>
                  <input required value={nomineeForm.nominee_name} onChange={(e) => setNomineeForm((prev) => ({ ...prev, nominee_name: e.target.value }))} className="w-full rounded-lg border border-slate-300 px-3 py-2" />
                </label>
                <label className="text-sm text-slate-700">
                  <span className="mb-1 block font-medium">Relationship</span>
                  <select required value={nomineeForm.relationship} onChange={(e) => setNomineeForm((prev) => ({ ...prev, relationship: e.target.value }))} className="w-full rounded-lg border border-slate-300 px-3 py-2">
                    <option value="">Select Relationship</option>
                    <option value="Mother">Mother</option>
                    <option value="Father">Father</option>
                    <option value="Daughter">Daughter</option>
                    <option value="Son">Son</option>
                    <option value="Widow">Widow</option>
                    <option value="Unmarried Daughter">Unmarried Daughter</option>
                    <option value="Minor Son up to 25 years">Minor Son up to 25 years</option>
                  </select>
                </label>
                <label className="text-sm text-slate-700">
                  <span className="mb-1 block font-medium">Date of Birth</span>
                  <input required type="date" value={nomineeForm.date_of_birth} onChange={(e) => setNomineeForm((prev) => ({ ...prev, date_of_birth: e.target.value }))} className="w-full rounded-lg border border-slate-300 px-3 py-2" />
                </label>
                <label className="text-sm text-slate-700">
                  <span className="mb-1 block font-medium">Gender</span>
                  <select value={nomineeForm.gender} onChange={(e) => setNomineeForm((prev) => ({ ...prev, gender: e.target.value }))} className="w-full rounded-lg border border-slate-300 px-3 py-2">
                    <option value="male">Male</option>
                    <option value="female">Female</option>
                    <option value="other">Other</option>
                  </select>
                </label>
                <label className="text-sm text-slate-700">
                  <span className="mb-1 block font-medium">Benefit (%)</span>
                  <input required type="number" min="1" max="100" value={nomineeForm.benefit_percentage} onChange={(e) => setNomineeForm((prev) => ({ ...prev, benefit_percentage: e.target.value }))} className="w-full rounded-lg border border-slate-300 px-3 py-2" />
                </label>
                <label className="text-sm text-slate-700">
                  <span className="mb-1 block font-medium">Priority</span>
                  <input required type="number" min="1" value={nomineeForm.priority} onChange={(e) => setNomineeForm((prev) => ({ ...prev, priority: e.target.value }))} className="w-full rounded-lg border border-slate-300 px-3 py-2" />
                </label>
                <label className="text-sm text-slate-700">
                  <span className="mb-1 block font-medium">Bank Name</span>
                  <input value={nomineeForm.bank_name} onChange={(e) => setNomineeForm((prev) => ({ ...prev, bank_name: e.target.value }))} className="w-full rounded-lg border border-slate-300 px-3 py-2" />
                </label>
                <label className="text-sm text-slate-700">
                  <span className="mb-1 block font-medium">Account Holder</span>
                  <input value={nomineeForm.account_holder_name} onChange={(e) => setNomineeForm((prev) => ({ ...prev, account_holder_name: e.target.value }))} className="w-full rounded-lg border border-slate-300 px-3 py-2" />
                </label>
                <label className="text-sm text-slate-700">
                  <span className="mb-1 block font-medium">Account Number</span>
                  <input value={nomineeForm.account_number} onChange={(e) => setNomineeForm((prev) => ({ ...prev, account_number: e.target.value }))} className="w-full rounded-lg border border-slate-300 px-3 py-2" />
                </label>
                <label className="text-sm text-slate-700">
                  <span className="mb-1 block font-medium">IFSC Code</span>
                  <input value={nomineeForm.ifsc_code} onChange={(e) => setNomineeForm((prev) => ({ ...prev, ifsc_code: e.target.value }))} className="w-full rounded-lg border border-slate-300 px-3 py-2" />
                </label>
                <label className="text-sm text-slate-700 md:col-span-2">
                  <span className="mb-1 block font-medium">Remarks</span>
                  <textarea value={nomineeForm.remarks} onChange={(e) => setNomineeForm((prev) => ({ ...prev, remarks: e.target.value }))} className="w-full rounded-lg border border-slate-300 px-3 py-2" rows="3" />
                </label>
                <div className="md:col-span-2 flex items-center justify-between">
                  <label className="flex items-center gap-2 text-sm text-slate-700">
                    <input type="checkbox" checked={nomineeForm.is_primary} onChange={(e) => setNomineeForm((prev) => ({ ...prev, is_primary: e.target.checked }))} />
                    Primary nominee
                  </label>
                  <button type="submit" disabled={submittingNominee} className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-60">
                    <PlusCircle className="h-4 w-4" />
                    {submittingNominee ? 'Saving...' : 'Save Nominee'}
                  </button>
                </div>
              </form>
            </div>

            <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6">
              <h2 className="text-lg font-semibold text-slate-900 mb-4">Submitted Nominee Records</h2>
              {nominees.length === 0 ? (
                <EmptyState icon={HeartHandshake} title="No nominee records yet" description="Your nominee request will appear here once submitted for review." size="medium" />
              ) : (
                <div className="space-y-3">
                  {nominees.map((nominee) => (
                    <div key={nominee.id} className="rounded-xl border border-slate-200 p-4">
                      <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
                        <div>
                          <p className="font-semibold text-slate-900">{nominee.nominee_name}</p>
                          <p className="text-sm text-slate-500">{nominee.relationship} · {nominee.benefit_percentage}% benefit</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${nominee.status === 'approved' ? 'bg-emerald-100 text-emerald-700' : nominee.status === 'rejected' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}`}>
                            {nominee.status}
                          </span>
                          {nominee.is_primary ? <span className="rounded-full bg-blue-100 px-2.5 py-1 text-xs font-semibold text-blue-700">Primary</span> : null}
                        </div>
                      </div>
                      <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3 text-sm text-slate-600">
                        <div><span className="font-medium text-slate-700">DOB:</span> {nominee.date_of_birth}</div>
                        <div><span className="font-medium text-slate-700">Bank:</span> {nominee.bank_name || '-'}</div>
                        <div><span className="font-medium text-slate-700">Account:</span> {nominee.account_number || '-'}</div>
                        <div><span className="font-medium text-slate-700">IFSC:</span> {nominee.ifsc_code || '-'}</div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {tab === 'documents' && (
          <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-lg font-semibold text-slate-900">Documents</h2>
                <p className="text-sm text-slate-500">Employee documents remain available for pensioners with the same employee record.</p>
              </div>
              <button
                type="button"
                onClick={loadProfile}
                className="inline-flex items-center gap-2 px-3 py-2 border border-slate-200 rounded-lg hover:bg-slate-50 text-sm"
              >
                <RefreshCw className="w-4 h-4" />
                Reload
              </button>
            </div>

            {documents.length === 0 ? (
              <EmptyState
                icon={FileText}
                title="No documents found"
                description="No active documents are linked to this employee record."
                size="medium"
              />
            ) : (
              <div className="space-y-3">
                {documents.map((doc) => (
                  <div key={doc.id} className="flex items-center justify-between p-4 rounded-xl border border-slate-200">
                    <div>
                      <p className="font-medium text-slate-900">{doc.type_name || 'Document'}</p>
                      <p className="text-xs text-slate-500">{doc.file_name}</p>
                      {doc.expiry_date && <p className="text-xs text-slate-500">Expires on {formatDate(doc.expiry_date)}</p>}
                    </div>
                    <button
                      type="button"
                      onClick={() => handleDownload(doc.id, doc.file_name)}
                      className="inline-flex items-center gap-2 px-3 py-2 border border-slate-200 rounded-lg hover:bg-slate-50 text-sm"
                    >
                      <Download className="w-4 h-4" />
                      Download
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {tab === 'support' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6">
              <h2 className="text-lg font-semibold text-slate-900 mb-4">Help & Support</h2>
              <div className="space-y-4 text-sm text-slate-700">
                <p>Use this dashboard to review your pension details, bank information, and documents.</p>
                <p>If anything looks incorrect, contact the HR or payroll team that manages your retirement records.</p>
                <p>Keep your bank account and document details up to date to avoid payout interruptions.</p>
              </div>
            </div>
            <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6">
              <h2 className="text-lg font-semibold text-slate-900 mb-4">Current Status</h2>
              <div className="space-y-3 text-sm">
                <div className="flex items-center justify-between">
                  <span className="text-slate-500">Account Type</span>
                  <span className="font-medium text-slate-900">Pensioner</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-slate-500">Pension Status</span>
                  <span className="font-medium text-slate-900">{profile.status || 'active'}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-slate-500">Retirement Type</span>
                  <span className="font-medium text-slate-900">{pensionSummary.retirement_type || '-'}</span>
                </div>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default PensionerDashboard;
